import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardBody, CardTitle, Table, Button, ButtonGroup, Progress } from 'reactstrap';
import { getCookie } from './utils';
import useBackgroundJob from 'components/useBackgroundJob';
import { formatEta } from 'components/jobEta';
import { METRICS, ALL, cellsFor, formatDate, formatPace, shortLabel, sportsOf, defaultSport, topRows } from './effortMetrics';

const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

const authHeaders = () => ({ Authorization: getCookie('stratava_token') });

function Top10() {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [metricField, setMetricField] = useState(METRICS[0].field);
  const [sportChoice, setSportChoice] = useState(null); // null = not chosen yet, use the default
  const [toAnalyze, setToAnalyze] = useState(0);
  const [extras, setExtras] = useState({}); // activityId -> { distanceKm, description, photos }
  const [extrasLimited, setExtrasLimited] = useState(false); // Strava's rate limit stopped the fetch of descriptions/photos
  const requested = useRef(new Set()); // ids already asked for, so switching metric does not ask again
  const mounted = useRef(true);

  const loadHistory = useCallback(() => {
    if (!getCookie('stratava_token')) {
      setError('Not authenticated. Please sign in on the Activities page first.');
      return Promise.resolve();
    }
    return axios
      .get(`${resource_api_url}/api/Get_Activity_History`, { headers: authHeaders() })
      .then((res) => mounted.current && setHistory(res.data))
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 440) {
          setError('Session expired. Please sign in again on the Activities page.');
        } else {
          setError('Failed to load your results. Please try again later.');
        }
      });
  }, []);

  // How many saved activities still wait for analysis (silent when nothing has been saved or the call fails)
  const loadStatus = useCallback(() => {
    axios
      .get(`${resource_api_url}/api/Sync_Status`, { headers: authHeaders() })
      .then((res) => mounted.current && setToAnalyze(res.data.toAnalyze || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadHistory();
    loadStatus();
    return () => {
      mounted.current = false;
    };
  }, [loadHistory, loadStatus]);

  // The analysis runs in the background on the server; the ticket is kept in the browser, so a running analysis
  // shows up again when this page is opened later.
  const { job, active: analysing, starting, error: jobError, start: analyse } = useBackgroundJob({
    ticketKey: 'stravalytics_analyze_ticket',
    startPath: '/api/Analyze_Start',
    startBody: {},
    onFinished: () => {
      loadHistory();
      loadStatus();
    },
  });

  // While the analysis runs, show the results that are already in
  const doneCount = job ? job.done : 0;
  useEffect(() => {
    if (analysing && doneCount > 0) {
      loadHistory();
      loadStatus();
    }
  }, [analysing, doneCount, loadHistory, loadStatus]);

  let message = null;
  if (jobError) {
    message = jobError;
  } else if (analysing) {
    const percent = job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
    const eta = formatEta(job);
    message = job.total == null
      ? 'Starting the analysis...'
      : `Analysing in the background: ${job.done} of ${job.total} (${percent}%)${eta ? `, ${eta}` : ''}. You can close this page.`;
  } else if (job && job.status === 'done') {
    message = job.total === 0
      ? 'Nothing new to analyse.'
      : `Done: ${job.analyzed} analysed${job.noDistance ? `, ${job.noDistance} skipped (no distance)` : ''}${job.failed ? `, ${job.failed} could not be analysed` : ''}.`;
  } else if (job && job.status === 'failed') {
    message = `The analysis stopped: ${job.error || 'unknown error'}. Start it again to continue.`;
  }
  const analyseProgress = analysing && job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;

  const metric = METRICS.find((m) => m.field === metricField);

  const sports = useMemo(() => sportsOf(history), [history]);
  const sport = sportChoice || defaultSport(sports);

  const top = useMemo(() => topRows(history, metric, sport), [history, metric, sport]);
  const hasSpeed = Boolean(metric.pace || metric.distanceKm); // std dev rows have no speed to compare

  // Distance, description and photos of the rows on screen (the server fetches them from Strava once and keeps them)
  const topIds = top.map((r) => r.ActivityId).join(',');
  useEffect(() => {
    const ids = topIds ? topIds.split(',').filter((id) => !requested.current.has(id)) : [];
    if (ids.length === 0) return;
    ids.forEach((id) => requested.current.add(id));
    axios
      .get(`${resource_api_url}/api/Get_Activity_Extras`, { headers: authHeaders(), params: { ids: ids.join(',') } })
      .then((res) => {
        if (!mounted.current) return;
        setExtras((prev) => ({ ...prev, ...res.data.extras }));
        setExtrasLimited(res.data.rateLimited);
        // rows that Strava could not deliver yet (rate limit) are asked for again next time
        Object.entries(res.data.extras).forEach(([id, e]) => e.pending && requested.current.delete(id));
      })
      .catch(() => ids.forEach((id) => requested.current.delete(id)));
  }, [topIds]);

  if (error) {
    return (
      <div className="content">
        <div className="forge-auth-panel">
          <div className="forge-subhead" style={{ marginBottom: '12px' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: 'var(--forge-warning)' }} />
            TOP 10 UNAVAILABLE
          </div>
          <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!history) {
    return (
      <div className="content">
        <div className="forge-status-msg">
          <i className="fas fa-circle-notch fa-spin" style={{ color: 'var(--forge-primary)', marginRight: '12px' }} />
          Loading your top 10...
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div style={{ marginBottom: '24px' }}>
        <div className="forge-headline" style={{ marginBottom: '4px' }}>
          <i className="tim-icons icon-trophy" style={{ marginRight: '10px', color: 'var(--forge-primary)', fontSize: '1.25rem' }} />
          TOP 10
        </div>
        <div className="forge-label" style={{ opacity: 0.6 }}>
          Based on the {history.length} {history.length === 1 ? 'activity' : 'activities'} you have analysed
        </div>
      </div>

      {(toAnalyze > 0 || analysing || message) && (
        <div className="forge-auth-panel" style={{ marginBottom: '24px' }}>
          {toAnalyze > 0 && !analysing && (
            <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)', fontSize: '0.875rem', marginBottom: '12px' }}>
              {toAnalyze} saved {toAnalyze === 1 ? 'activity is' : 'activities are'} not analysed yet.
            </p>
          )}
          {(toAnalyze > 0 || analysing) && (
            <Button color="info" size="sm" disabled={starting || analysing} onClick={analyse}>
              {starting || analysing ? (
                <>
                  <i className="fas fa-circle-notch fa-spin" style={{ marginRight: '8px' }} />
                  Analysing...
                </>
              ) : (
                'Analyse saved activities'
              )}
            </Button>
          )}
          {analysing && job.total > 0 && <Progress value={analyseProgress} style={{ marginTop: '12px', height: '6px' }} color="info" />}
          {message && (
            <p style={{ marginTop: toAnalyze > 0 || analysing ? '12px' : 0, marginBottom: 0, color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}>
              {message}
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <ButtonGroup style={{ flexWrap: 'wrap' }}>
          {METRICS.map((m) => (
            <Button key={m.field} color="info" size="sm" outline={m.field !== metricField} onClick={() => setMetricField(m.field)}>
              {m.label}
            </Button>
          ))}
        </ButtonGroup>
        {sports.length > 2 && (
          <ButtonGroup style={{ flexWrap: 'wrap' }}>
            {sports.map((s) => (
              <Button key={s} color="primary" size="sm" outline={s !== sport} onClick={() => setSportChoice(s)}>
                {s}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle tag="h4" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {metric.label}{sport !== ALL ? ` · ${sport}` : ''}
          </CardTitle>
          {metric.note && (
            <p className="card-category" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
              {metric.note}
            </p>
          )}
        </CardHeader>
        <CardBody>
          {top.length === 0 ? (
            <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>
              No results yet. Save your activities on the Activities page, then analyse them here.
            </p>
          ) : (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Activity</th>
                  <th>Date</th>
                  <th className="text-right">Distance km</th>
                  <th className="text-right">{metric.unit}</th>
                  {hasSpeed && <th className="text-right">{shortLabel(metric)} pace /km</th>}
                  {hasSpeed && <th className="text-right">Total pace /km</th>}
                  <th>Description</th>
                  <th>Photos</th>
                </tr>
              </thead>
              <tbody>
                {top.map((r, i) => {
                  const cells = cellsFor(metric, r.value);
                  const extra = extras[r.ActivityId];
                  return (
                  <tr key={r.ActivityId}>
                    <td>{i + 1}</td>
                    <td>
                      <a href={`https://www.strava.com/activities/${r.ActivityId}`} target="_blank" rel="noopener noreferrer">
                        {r.ActivityName || r.ActivityId}
                      </a>
                    </td>
                    <td>{formatDate(r.ActivityDate)}</td>
                    <td className="text-right">{extra && extra.distanceKm > 0 ? extra.distanceKm.toFixed(2) : ''}</td>
                    <td className="text-right">{cells.main}</td>
                    {hasSpeed && <td className="text-right">{cells.pace}</td>}
                    {hasSpeed && <td className="text-right">{extra && extra.avgSpeedKmh > 0 ? formatPace(extra.avgSpeedKmh) : ''}</td>}
                    <td style={{ minWidth: '180px', maxWidth: '320px', whiteSpace: 'normal' }}>
                      {!extra ? (
                        <i className="fas fa-circle-notch fa-spin" style={{ opacity: 0.4 }} />
                      ) : (
                        <span title={extra.description} style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {extra.description}
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minWidth: '96px' }}>
                        {extra && extra.photos.map((url) => (
                          <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt="" loading="lazy" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', display: 'block' }} />
                          </a>
                        ))}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {extrasLimited && (
            <p style={{ marginTop: '12px', marginBottom: 0, color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)', fontSize: '0.8rem' }}>
              Strava's rate limit was reached, so some descriptions and photos are missing. Reload this page in a few minutes.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default Top10;
